import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Users } from 'lucide-react';
import { qs } from '@/lib/api';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { DraftRowActions, DraftsFilter, useDecideDraft, useDraftCount, type CatalogueFilter } from '@/features/common/Drafts';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsPhone } from '@/hooks/useIsPhone';
import { PageHeader } from '@/app/DashboardLayout';
import { CustomerSheet } from './CustomerSheet';
import { AdvancedFilter, useAdvancedFilter } from '@/features/common/AdvancedFilter';
import {
  Badge, Button, Card, CardLine, CardRow, Cards, EmptyState, ErrorState, Input, Money,
  SkeletonTable, TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface CustomerRow {
  id: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  currency: string | null;
  gstin: string | null;
  isActive: boolean;
  outstanding: string;
  /** 'draft' when the AI agent proposed this row and nobody has confirmed it. See Drafts.tsx. */
  status?: 'active' | 'draft';
  isDraft?: boolean;
  proposalNote?: string | null;
}

export function CustomersPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  // A search result opens this list already narrowed to it (`?q=`).
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '');
  const [composerOpen, setComposerOpen] = useState(false);
  /** The customer being edited in the sheet, when one is. */
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The confirmed list, or the drafts the AI agent proposed. */
  const [filter, setFilter] = useState<CatalogueFilter>('active');
  const draftCount = useDraftCount('customers');
  const decide = useDecideDraft('customer', 'customers');
  // The condition builder, in the URL: "registered businesses in Tamil Nadu
  // with no email" is a link.
  const advanced = useAdvancedFilter();

  const query = usePagedList<CustomerRow, { scopedToSelf: boolean }>({
    // Plural resource for a list, orgId always second, then the specifiers.
    // Invalidating ['customers', orgId] clears every search variant under it.
    key: ['customers', org?.id, search, filter, advanced.key],
    // The default list is active rows only - the server's default too - and
    // a draft is listed only when asked for by name.
    path: (cursor) => `/customers${qs({ q: search, status: filter === 'draft' ? 'draft' : undefined, cursor, ...advanced.params })}`,
    enabled: !!org?.id,
  });

  const currency = org ? 'INR' : 'INR';

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Everyone you invoice, and what they still owe you."
        actions={
          perms.can('customer', 'create') ? (
            <Button size="sm" onClick={() => setComposerOpen(true)}>
              <Plus className="size-4" aria-hidden />
              New customer
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            className="pl-8"
            placeholder="Search by name, email or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search customers"
          />
        </div>

        <DraftsFilter value={filter} count={draftCount} onChange={setFilter} noun="customers" />

        <AdvancedFilter entityType="customer" value={advanced.value} onChange={advanced.set} />

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : query.items.length ? (
            <>
            {isPhone ? (
            <Cards>
              {query.items.map((customer) => {
                const line = (
                  <CardLine
                    title={
                      <span className="flex items-center gap-2">
                        <span className="truncate">{customer.displayName}</span>
                        {customer.isDraft ? <Badge variant="warning">Draft</Badge> : !customer.isActive ? <Badge>Inactive</Badge> : null}
                      </span>
                    }
                    meta={customer.email ?? customer.gstin ?? undefined}
                    value={customer.isDraft ? undefined : <Money value={customer.outstanding} currency={customer.currency ?? currency} />}
                    sub={customer.isDraft ? undefined : 'outstanding'}
                  />
                );
                return (
                  <CardRow key={customer.id} data-testid="customer-row" data-draft={customer.isDraft ? 'true' : undefined}>
                    {/* A draft has no statement to open: nothing has been raised against it. */}
                    {customer.isDraft ? (
                      <>
                        {line}
                        <DraftRowActions id={customer.id} name={customer.displayName} decide={decide} note={customer.proposalNote} compact />
                      </>
                    ) : (
                      <>
                        <Link to={`/customers/${customer.id}/ledger`} className="block">
                          {line}
                        </Link>
                        {perms.can('customer', 'edit') ? (
                          <div className="mt-1 flex justify-end">
                            <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(customer.id)} aria-label={`Edit ${customer.displayName}`} data-testid="customer-edit">
                              <Pencil className="size-3.5" aria-hidden />
                              Edit
                            </Button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </CardRow>
                );
              })}
            </Cards>
            ) : (

                        <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Email</TH>
                  <TH>GSTIN</TH>
                  <TH numeric>Outstanding</TH>
                  <TH>{''}</TH>
                </TR>
              </THead>
              <TBody>
                {query.items.map((customer) => (
                  <TR key={customer.id} data-testid="customer-row" data-draft={customer.isDraft ? 'true' : undefined}>
                    <TD>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{customer.displayName}</span>
                        {customer.isDraft ? <Badge variant="warning">Draft</Badge> : !customer.isActive ? <Badge>Inactive</Badge> : null}
                      </div>
                      {customer.companyName && customer.companyName !== customer.displayName ? (
                        <p className="text-xs text-muted-foreground">{customer.companyName}</p>
                      ) : null}
                      {customer.isDraft ? (
                        <DraftRowActions id={customer.id} name={customer.displayName} decide={decide} note={customer.proposalNote} />
                      ) : null}
                    </TD>
                    <TD className="text-muted-foreground">{customer.email ?? '—'}</TD>
                    <TD className="font-mono text-xs text-muted-foreground">{customer.gstin ?? '—'}</TD>
                    <TD numeric>
                      {customer.isDraft ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Money value={customer.outstanding} currency={customer.currency ?? currency} />
                      )}
                    </TD>
                    <TD>
                      {/* The first thing asked for in any dispute - once there is one. A draft has nothing raised against it. */}
                      {customer.isDraft ? null : (
                        <div className="flex items-center justify-end gap-3">
                          <Link
                            to={`/customers/${customer.id}/ledger`}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            Statement
                          </Link>
                          {perms.can('customer', 'edit') ? (
                            <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(customer.id)} aria-label={`Edit ${customer.displayName}`} data-testid="customer-edit">
                              <Pencil className="size-3.5" aria-hidden />
                              Edit
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            )}
            <ListFooter query={query} noun="customers" />
            </>
          ) : (
            <EmptyState
              icon={Users}
              title={filter === 'draft' ? 'No drafts waiting' : search || advanced.active ? 'No customers match that' : 'No customers yet'}
              description={
                filter === 'draft'
                  ? 'When your AI agent proposes a customer, it shows here - and in the Inbox - until you confirm it.'
                  : search || advanced.active
                    ? 'Try a different name, email or phone number, or loosen the filter.'
                    : 'Add the first person or business you invoice.'
              }
              action={
                filter !== 'draft' && !search && !advanced.active && perms.can('customer', 'create') ? (
                  <Button size="sm" onClick={() => setComposerOpen(true)}>
                    <Plus className="size-4" aria-hidden />
                    New customer
                  </Button>
                ) : null
              }
            />
          )}
        </Card>

        {query.first?.scopedToSelf ? (
          // Say so out loud rather than showing a quietly narrowed list. A user
          // who cannot see a colleague's customer should learn the boundary
          // exists, not conclude the customer was deleted.
          <p className="text-xs text-muted-foreground">
            You are seeing only the customers you own. An administrator can widen this.
          </p>
        ) : null}
      </div>

      {composerOpen || editingId ? (
        <CustomerSheet
          customerId={editingId ?? undefined}
          onClose={() => {
            setComposerOpen(false);
            setEditingId(null);
          }}
          onSaved={() => {
            setComposerOpen(false);
            setEditingId(null);
            // Prefix-matched, so this clears every cached search variant.
            void queryClient.invalidateQueries({ queryKey: ['customers', org?.id] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
          }}
        />
      ) : null}
    </>
  );
}
